import { Document } from "@prismicio/editor-fields"
import { getRequestCredentials, request } from "@prismicio/editor-support/Http"
import { queryOptions } from "@tanstack/react-query"
import { z } from "zod"

import { getDevToken } from "../auth/devToken"
import { queryClient } from "../components/providers/QueryProvider"
import { env } from "../env"
import { getRepositoryName } from "../routing"
import { socketRequest } from "./ws"

interface TranslateDocumentArgs {
  uuid: string
  document: Document
  sourceLocaleId: string
  targetLocaleId: string
  additionalInstructions?: string
  onMessage: (message: TranslateDocumentMessage) => void
  isObelixAiTranslateDocumentEnabled: boolean
}

export function translateDocument(args: TranslateDocumentArgs) {
  const {
    uuid,
    document,
    sourceLocaleId,
    targetLocaleId,
    additionalInstructions,
    onMessage,
    isObelixAiTranslateDocumentEnabled,
  } = args

  const repository = getRepositoryName()

  const wsUrl = isObelixAiTranslateDocumentEnabled
    ? new URL(env.PRISMIC_DOCUMENT_WEBSOCKET_BASE_URL)
    : new URL(env.PRISMIC_AI_SERVICE_WEBSOCKET_ENDPOINT)

  wsUrl.searchParams.set("repository", repository)
  wsUrl.searchParams.set("uuid", uuid)

  if (env.PRISMIC_PAGE_BUILDER_AUTH_STRATEGY === "token") {
    wsUrl.searchParams.set("authorization", getDevToken())
  }

  const translateDocumentUrl = isObelixAiTranslateDocumentEnabled
    ? new URL("translate-document", env.PRISMIC_DOCUMENT_BASE_URL)
    : new URL("translate-document", env.PRISMIC_AI_SERVICE_BASE_URL)

  translateDocumentUrl.searchParams.set("repository", repository)

  socketRequest({
    url: wsUrl,
    onOpen: () =>
      request(translateDocumentUrl, {
        credentials: getRequestCredentials(
          env.PRISMIC_PAGE_BUILDER_AUTH_STRATEGY,
        ),
        method: "POST",
        body: {
          uuid,
          document,
          sourceLocaleId,
          targetLocaleId,
          // TODO: locale kept for backwards compatibility,
          // remove once Obelix that handles targetLocaleId is on prod
          // ref: https://github.com/prismicio/obelix/pull/591
          locale: targetLocaleId,
          additionalInstructions,
        },
        okType: z.unknown(),
      }),
    schema: translateDocumentMessageSchema,
    onMessage: (message) => {
      onMessage(message)
      switch (message.status) {
        case "translating":
          return { done: false }
        case "done":
        case "failed":
          return { done: true }
      }
    },
    onError: () => onMessage({ status: "failed" }),
  })
}

const translateDocumentMessageSchema = z.discriminatedUnion("status", [
  z.object({ status: z.literal("translating") }),
  z.object({
    status: z.literal("done"),
    document: Document,
    model: z.string(),
    cost: z.number(),
    tokensIn: z.number(),
    tokensOut: z.number(),
  }),
  z.object({ status: z.literal("failed") }),
])

type TranslateDocumentMessage = z.infer<typeof translateDocumentMessageSchema>

const GetAiTranslationFeedbackResponse = z.object({
  repositorySubmitted: z.boolean(),
  userSubmitted: z.boolean(),
})

type GetAiTranslationFeedbackResponse = z.infer<
  typeof GetAiTranslationFeedbackResponse
>

function getAiTranslationFeedback() {
  const url = new URL("ai-translation-feedback", env.PRISMIC_DOCUMENT_BASE_URL)
  url.searchParams.append("repository", getRepositoryName())

  return request(url, {
    method: "GET",
    credentials: getRequestCredentials(env.PRISMIC_PAGE_BUILDER_AUTH_STRATEGY),
    okType: GetAiTranslationFeedbackResponse,
  })
}

export function getAiTranslationFeedbackOptions() {
  return queryOptions({
    queryKey: ["aiTranslationFeedback"],
    queryFn: () => getAiTranslationFeedback(),
  })
}

export function invalidateAiTranslationFeedback() {
  return queryClient.invalidateQueries({
    queryKey: ["aiTranslationFeedback"],
  })
}

export function formValueToNumber<T extends z.ZodType>(schema: T) {
  return z.preprocess((value) => {
    if (value === null || value === undefined) return undefined
    const valueAsNumber = Number(value)
    if (isNaN(valueAsNumber)) return undefined
    return valueAsNumber
  }, schema)
}

const BaseAiTranslationFeedbackBody = z.object({
  translationQualitySatisfaction: formValueToNumber(
    z.number().int().min(1).max(5),
  ),
  whatWorkedWell: z.string().min(1, { message: "Required" }),
  whatCouldBeImproved: z.string().min(1, { message: "Required" }),
  pagesTranslatedPerMonth: formValueToNumber(z.number().int().min(0)),
})

const PerWordAiTranslationFeedbackBody = BaseAiTranslationFeedbackBody.extend({
  formVariant: z.literal("perWord"),
  priceTooCheapPerWord: formValueToNumber(z.number().min(0)),
  priceGoodValuePerWord: formValueToNumber(z.number().min(0)),
  priceGettingExpensivePerWord: formValueToNumber(z.number().min(0)),
  priceTooExpensivePerWord: formValueToNumber(z.number().min(0)),
})

const PerPageAiTranslationFeedbackBody = BaseAiTranslationFeedbackBody.extend({
  formVariant: z.literal("perPage"),
  priceTooCheapPerPage: formValueToNumber(z.number().min(0)),
  priceGoodValuePerPage: formValueToNumber(z.number().min(0)),
  priceGettingExpensivePerPage: formValueToNumber(z.number().min(0)),
  priceTooExpensivePerPage: formValueToNumber(z.number().min(0)),
})

export const AiTranslationFeedbackBody = z.discriminatedUnion("formVariant", [
  PerWordAiTranslationFeedbackBody,
  PerPageAiTranslationFeedbackBody,
])

export type AiTranslationFeedbackBody = z.infer<
  typeof AiTranslationFeedbackBody
>

interface PostAiTranslationFeedbackArgs {
  body: AiTranslationFeedbackBody
}

export function postAiTranslationFeedback(
  args: PostAiTranslationFeedbackArgs,
): Promise<unknown> {
  const { body } = args

  const url = new URL("ai-translation-feedback", env.PRISMIC_DOCUMENT_BASE_URL)
  url.searchParams.append("repository", getRepositoryName())

  return request(url, {
    method: "POST",
    body,
    credentials: getRequestCredentials(env.PRISMIC_PAGE_BUILDER_AUTH_STRATEGY),
    okType: z.unknown(),
  })
}
